/**
 * GitHub agent tools, generated Git text, and commit metadata.
 *
 * @module
 */
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import {
  getCustomModelOptionsByProvider,
  resolveAppModelSelectionState,
} from "../../modelSelection";
import { useServerProviders } from "../../rpc/serverState";
import { ProviderModelPicker } from "../chat/ProviderModelPicker";
import { PROVIDER_AVAILABILITY_PAYLOAD_ONLY } from "../chat/modelPickerProviderAvailability";
import { TraitsPicker } from "../chat/TraitsPicker";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { SettingResetButton, SettingsRow, SettingsSection } from "./settingsLayout";
import { ModelSelection } from "@cmmd-forge/contracts";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import { Equal } from "effect";

export function GitSection() {
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const serverProviders = useServerProviders();

  const textGenerationModelSelection = resolveAppModelSelectionState(settings, serverProviders);
  const textGenProvider = textGenerationModelSelection.provider;
  const textGenModel = textGenerationModelSelection.model;
  const textGenModelOptions = textGenerationModelSelection.options;
  const gitModelOptionsByProvider = getCustomModelOptionsByProvider(
    settings,
    serverProviders,
    textGenProvider,
    textGenModel,
  );
  const isGitWritingModelDirty = !Equal.equals(
    settings.textGenerationModelSelection ?? null,
    DEFAULT_UNIFIED_SETTINGS.textGenerationModelSelection ?? null,
  );

  return (
    <SettingsSection id="git" title="Source Control">
      <SettingsRow
        title="Text generation model"
        description="Configure the model used for generated commit messages, PR titles, and similar Git text."
        resetAction={
          isGitWritingModelDirty ? (
            <SettingResetButton
              label="text generation model"
              onClick={() =>
                updateSettings({
                  textGenerationModelSelection:
                    DEFAULT_UNIFIED_SETTINGS.textGenerationModelSelection,
                })
              }
            />
          ) : null
        }
        control={
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <ProviderModelPicker
              provider={textGenProvider}
              model={textGenModel}
              lockedProvider={null}
              providers={serverProviders}
              providerAvailability={PROVIDER_AVAILABILITY_PAYLOAD_ONLY}
              modelOptionsByProvider={gitModelOptionsByProvider}
              triggerVariant="outline"
              triggerClassName="min-w-0 max-w-none shrink-0 text-foreground/90 hover:text-foreground"
              onProviderModelChange={(provider, model) => {
                updateSettings({
                  textGenerationModelSelection: resolveAppModelSelectionState(
                    {
                      ...settings,
                      textGenerationModelSelection: { provider, model },
                    },
                    serverProviders,
                  ),
                });
              }}
            />
            <TraitsPicker
              provider={textGenProvider}
              models={
                serverProviders.find((provider) => provider.provider === textGenProvider)?.models ??
                []
              }
              model={textGenModel}
              prompt=""
              onPromptChange={() => {}}
              modelOptions={textGenModelOptions}
              allowPromptInjectedEffort={false}
              triggerVariant="outline"
              triggerClassName="min-w-0 max-w-none shrink-0 text-foreground/90 hover:text-foreground"
              onModelOptionsChange={(nextOptions) => {
                updateSettings({
                  textGenerationModelSelection: resolveAppModelSelectionState(
                    {
                      ...settings,
                      textGenerationModelSelection: {
                        provider: textGenProvider,
                        model: textGenModel,
                        ...(nextOptions ? { options: nextOptions } : {}),
                      } as ModelSelection,
                    },
                    serverProviders,
                  ),
                });
              }}
            />
          </div>
        }
      />

      <SettingsRow
        title="Co-author commits with Forge"
        description="Add a Co-Authored-By trailer pointing at the cmmd-forge GitHub App so commits render as 'cmmd-forge[bot] and you' on GitHub."
        resetAction={
          settings.coauthorIncludeForge !== DEFAULT_UNIFIED_SETTINGS.coauthorIncludeForge ? (
            <SettingResetButton
              label="Forge co-author"
              onClick={() =>
                updateSettings({
                  coauthorIncludeForge: DEFAULT_UNIFIED_SETTINGS.coauthorIncludeForge,
                })
              }
            />
          ) : null
        }
        control={
          <Switch
            checked={settings.coauthorIncludeForge}
            onCheckedChange={(checked) =>
              updateSettings({ coauthorIncludeForge: Boolean(checked) })
            }
            aria-label="Include Forge as commit co-author"
          />
        }
      />

      <SettingsRow
        title="GitHub write identity"
        description="Choose who Forge acts as when pushing commits and opening pull requests. Independent of the co-author setting above, which only decorates the commit message."
        resetAction={
          settings.githubWriteIdentity !== DEFAULT_UNIFIED_SETTINGS.githubWriteIdentity ? (
            <SettingResetButton
              label="GitHub write identity"
              onClick={() =>
                updateSettings({
                  githubWriteIdentity: DEFAULT_UNIFIED_SETTINGS.githubWriteIdentity,
                })
              }
            />
          ) : null
        }
        control={
          <Select
            value={settings.githubWriteIdentity}
            onValueChange={(value) => {
              if (value === "forge-bot" || value === "my-account") {
                updateSettings({ githubWriteIdentity: value });
              }
            }}
          >
            <SelectTrigger className="w-full sm:w-56" aria-label="GitHub write identity">
              <SelectValue>
                {settings.githubWriteIdentity === "my-account"
                  ? "Your GitHub account"
                  : "cmmd-forge[bot]"}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup align="end" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value="forge-bot">
                cmmd-forge[bot] — Forge acts as the bot on your behalf
              </SelectItem>
              <SelectItem hideIndicator value="my-account">
                Your GitHub account — Forge acts as you
              </SelectItem>
            </SelectPopup>
          </Select>
        }
      />
    </SettingsSection>
  );
}
